import { StateEffect, StateField, type Extension } from "@codemirror/state";
import { Decoration, type DecorationSet, EditorView } from "@codemirror/view";
import type { SourceRange } from "@magic-md/engine-client/protocol";
import { sourceRangeToOffsets } from "./helpers";

export const setIntelligencePreviewRange =
    StateEffect.define<SourceRange | null>();

function buildPreviewDecorations(
    state: EditorView["state"],
    range: SourceRange | null,
): DecorationSet {
    if (!range) return Decoration.none;
    if (range.startLine < 1 || range.startLine > state.doc.lines) {
        return Decoration.none;
    }

    const decorations: Array<{
        from: number;
        to: number;
        value: ReturnType<typeof Decoration.line> | ReturnType<typeof Decoration.mark>;
    }> = [];
    const endLine = Math.min(range.endLine, state.doc.lines);
    for (let lineNumber = range.startLine; lineNumber <= endLine; lineNumber++) {
        const line = state.doc.line(lineNumber);
        decorations.push({
            from: line.from,
            to: line.from,
            value: Decoration.line({
                class: "cm-intelligence-preview-line",
            }),
        });
    }

    const offsets = sourceRangeToOffsets(state.doc, range);
    if (offsets && offsets.to > offsets.from) {
        decorations.push({
            from: offsets.from,
            to: offsets.to,
            value: Decoration.mark({
                class: "cm-intelligence-preview-range",
            }),
        });
    }

    return Decoration.set(
        decorations.map((entry) => entry.value.range(entry.from, entry.to)),
        true,
    );
}

export const intelligencePreviewField = StateField.define<DecorationSet>({
    create: () => Decoration.none,
    update(value, tr) {
        let next = value.map(tr.changes);
        for (const effect of tr.effects) {
            if (effect.is(setIntelligencePreviewRange)) {
                next = buildPreviewDecorations(tr.state, effect.value);
            }
        }
        return next;
    },
    provide: (field) => EditorView.decorations.from(field),
});

export const intelligencePreviewTheme = EditorView.baseTheme({
    ".cm-intelligence-preview-line": {
        backgroundColor:
            "color-mix(in oklab, var(--color-info) 8%, transparent)",
    },
    ".cm-intelligence-preview-range": {
        borderRadius: "3px",
        backgroundColor:
            "color-mix(in oklab, var(--color-info) 18%, transparent)",
        boxShadow:
            "inset 0 0 0 1px color-mix(in oklab, var(--color-info) 22%, transparent)",
    },
});

export function intelligencePreviewHighlight(): Extension[] {
    return [intelligencePreviewField, intelligencePreviewTheme];
}
